/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import type {
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  SerializedEditor,
  SerializedLexicalNode,
  Spread
} from 'lexical';

import { $applyNodeReplacement, createEditor, DecoratorNode } from 'lexical';
import * as React from 'react';
import { Suspense } from 'react';
import AvatarDefault from '../../../../assets/medium_default_optimized.png';

const ImageComponent = React.lazy(() => import('./ImageComponent'));

export interface ImagePayload {
  altText: string;
  caption?: LexicalEditor;
  height?: number;
  key?: NodeKey;
  maxWidth?: number;
  showCaption?: boolean;
  src: string;
  width?: number;
  captionsEnabled?: boolean;
  imageType?: string;
  contributors?: any;
  contributorId?: string;
  isAddedFromContributorList?: boolean;
}

function isGoogleDocCheckboxImg(img: HTMLImageElement): boolean {
  return (
    img.parentElement != null &&
    img.parentElement.tagName === 'LI' &&
    img.previousSibling === null &&
    img.getAttribute('aria-roledescription') === 'checkbox'
  );
}

function $convertImageElement(domNode: Node): null | DOMConversionOutput {
  const img = domNode as HTMLImageElement;
  if (img.src.startsWith('file:///') || isGoogleDocCheckboxImg(img)) {
    return null;
  }
  const { alt: altText, src, width, height } = img;
  const node = $createImageNode({ altText, height, src, width });
  return { node };
}

export type SerializedImageNode = Spread<
  {
    altText: string;
    caption: SerializedEditor;
    height?: number;
    maxWidth: number;
    showCaption: boolean;
    src: string;
    width?: number;
    imageType: string;
    contributorId: string;
    contributors: any;
    isAddedFromContributorList?: boolean;
  },
  SerializedLexicalNode
>;

export class ImageNode extends DecoratorNode<JSX.Element> {
  __src: string;
  __altText: string;
  __width: 'inherit' | number;
  __height: 'inherit' | number;
  __maxWidth: number;
  __showCaption: boolean;
  __caption: LexicalEditor;
  // Captions cannot yet be used within editor cells
  __captionsEnabled: boolean;
  __imageType: string;
  __contributors: any;
  __contributorId: string;
  __isAddedFromContributorList: boolean;

  static getType(): string {
    return 'image';
  }

  static clone(node: ImageNode): ImageNode {
    return new ImageNode(
      node.__src,
      node.__altText,
      node.__maxWidth,
      node.__width,
      node.__height,
      node.__showCaption,
      node.__caption,
      node.__captionsEnabled,
      node.getKey(), // Ensure the key is passed
      node.__imageType,
      node.__contributors,
      node.__contributorId,
      node.__isAddedFromContributorList
    );
  }

  static importJSON(serializedNode: SerializedImageNode): ImageNode {
    const {
      imageType,
      altText,
      height,
      width,
      maxWidth,
      caption,
      src,
      showCaption,
      contributorId,
      contributors,
      isAddedFromContributorList
    } = serializedNode;
    const node = $createImageNode({
      altText,
      height,
      maxWidth,
      showCaption,
      src,
      width,
      imageType,
      contributorId,
      contributors,
      isAddedFromContributorList
    });
    const nestedEditor = node.__caption;
    const editorState = nestedEditor.parseEditorState(caption.editorState);
    if (!editorState.isEmpty()) {
      nestedEditor.setEditorState(editorState);
    }
    return node;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('img');
    element.setAttribute('src', this.__src);
    element.setAttribute('alt', this.__altText);
    element.setAttribute('width', this.__width.toString());
    element.setAttribute('height', this.__height.toString());
    return { element };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      img: (node: Node) => ({
        conversion: $convertImageElement,
        priority: 0
      })
    };
  }

  constructor(
    src: string,
    altText: string,
    maxWidth: number,
    width?: 'inherit' | number,
    height?: 'inherit' | number,
    showCaption?: boolean,
    caption?: LexicalEditor,
    captionsEnabled?: boolean,
    key?: NodeKey,
    imageType?: string,
    contributors?: any,
    contributorId?: string,
    isAddedFromContributorList?: boolean
  ) {
    super(key);
    this.__src = src;
    this.__altText = altText;
    this.__maxWidth = maxWidth;
    this.__width = width || 'inherit';
    this.__height = height || 'inherit';
    this.__showCaption = showCaption || false;
    this.__caption =
      caption ||
      createEditor({
        nodes: []
      });
    this.__captionsEnabled = captionsEnabled || captionsEnabled === undefined;
    this.__imageType = imageType;
    this.__contributors = contributors;
    this.__contributorId = contributorId;
    this.__isAddedFromContributorList = isAddedFromContributorList;
  }

  exportJSON(): SerializedImageNode {
    return {
      altText: this.getAltText(),
      caption: this.__caption.toJSON(),
      height: this.__height === 'inherit' ? 0 : this.__height,
      maxWidth: this.__maxWidth,
      showCaption: this.__showCaption,
      src: this.__src,
      type: 'image',
      version: 1,
      width: this.__width === 'inherit' ? 0 : this.__width,
      imageType: this.__imageType,
      contributors: this.__contributors,
      contributorId: this.__contributorId,
      isAddedFromContributorList: this.__isAddedFromContributorList
    };
  }

  setWidthAndHeight(width: 'inherit' | number, height: 'inherit' | number): void {
    const writable = this.getWritable();
    writable.__width = width;
    writable.__height = height;
  }

  setShowCaption(showCaption: boolean): void {
    const writable = this.getWritable();
    writable.__showCaption = showCaption;
  }

  getContributorId() {
    return this.__contributorId;
  }

  // View

  createDOM(config: EditorConfig): HTMLElement {
    const span = document.createElement('span');
    const theme = config.theme;
    const className = theme.media;
    if (className !== undefined) {
      span.className = className;
    }
    return span;
  }

  updateDOM(): false {
    return false;
  }

  getSrc(): string {
    return this.__src;
  }

  getAltText(): string {
    return this.__altText;
  }

  decorate(): JSX.Element {
    return (
      <Suspense fallback={null}>
        <ImageComponent
          src={this.__src}
          altText={this.__altText}
          width={this.__width}
          height={this.__height}
          maxWidth={this.__maxWidth}
          nodeKey={this.getKey()}
          showCaption={this.__showCaption}
          contributors={this.__contributors}
          caption={this.__caption}
          isAddedFromContributorList={this.__isAddedFromContributorList}
          // captionsEnabled={this.__captionsEnabled}
          resizable={true}
        />
        {this.__isAddedFromContributorList && (
          <div className="flex flex-wrap items-center justify-center gap-2 mt-[10px] font-ui text-base">
            <p className="text-dim-gray font-[600]">{this.__altText}</p>
            <div className="flex items-center gap-2">
              <span className="text-dim-gray font-[600] whitespace-nowrap">📷 Photo By:</span>
              <div
                className="flex items-start gap-2 text-jet-black font-[600] cursor-pointer"
                onClick={() => window.open(`/${this.__contributors.username}`)}
              >
                <img
                  className="w-6 h-6 object-cover rounded-full flex-shrink-0"
                  src={this.__contributors?.imgUrl || AvatarDefault}
                  alt=""
                />
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    title={this.__contributors?.firstName}
                    className="hover:text-growth-green transition-all max-w-[100px] truncate duration-200"
                  >
                    {this.__contributors?.firstName}
                  </span>
                  {this.__contributors?.username && (
                    <span className="font-[600] text-xs text-02-dim-gray whitespace-nowrap">
                      @{this.__contributors?.username}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </Suspense>
    );
  }
}

export function $createImageNode({
  altText,
  height,
  maxWidth,
  captionsEnabled,
  src,
  width,
  showCaption,
  caption,
  key,
  imageType,
  contributors,
  contributorId,
  isAddedFromContributorList
}: ImagePayload): ImageNode {
  return $applyNodeReplacement(
    new ImageNode(
      src,
      altText,
      maxWidth,
      width,
      height,
      showCaption,
      caption,
      captionsEnabled,
      key,
      imageType,
      contributors,
      contributorId,
      isAddedFromContributorList
    )
  );
}

export function $isImageNode(node: LexicalNode | null | undefined): node is ImageNode {
  return node instanceof ImageNode;
}
